import { useState, useEffect } from 'react';
import { getBookings, refundBooking, deleteBooking } from '../services/api';
import api from '../services/api';

const assignWorker = (bookingId, workerId) => 
  api.put(`/admin/bookings/${bookingId}/assign`, { worker_id: workerId }).then(r => r.data);

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [assigningId, setAssigningId] = useState(null);
  const [workerId, setWorkerId] = useState('');

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    setLoading(true);
    try { setBookings(await getBookings()); } catch (e) {}
    setLoading(false);
  };

  const handleAssign = async (bookingId) => {
    if (!workerId) { alert('Enter worker ID'); return; }
    try { await assignWorker(bookingId, parseInt(workerId)); alert('Worker assigned!'); setAssigningId(null); setWorkerId(''); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleRefund = async (id) => {
    if (!window.confirm('Issue refund for this booking?')) return;
    try { await refundBooking(id); alert('Refund issued!'); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Refund failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Permanently delete Booking #${id}? This cannot be undone.`)) return;
    try { await deleteBooking(id); alert('Booking deleted!'); loadBookings(); }
    catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'badge-grey',
      confirmed: 'badge-info',
      in_progress: 'badge-warning',
      completed: 'badge-success',
      cancelled: 'badge-danger',
    };
    return <span className={`badge ${map[status] || 'badge-grey'}`}>{status}</span>;
  };

  const filtered = (filter === 'all' ? bookings : bookings.filter(b => b.status === filter))
    .filter(b => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return String(b.id).includes(q) ||
             (b.display_id || '').toLowerCase().includes(q) ||
             (b.customer?.name || '').toLowerCase().includes(q) ||
             (b.customer?.phone || '').includes(q) ||
             (b.worker?.user?.name || '').toLowerCase().includes(q) ||
             (b.service?.name || '').toLowerCase().includes(q);
    });

  return (
    <div>
      <div className="page-header">
        <h1>Bookings Management</h1>
        <p>View and manage all customer bookings</p>
      </div>
      <div className="card">
        <div className="card-header">
          <h2>📋 All Bookings ({filtered.length})</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              placeholder="🔍 Search by ID, customer, worker, service..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, width: 280 }}
            />
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13 }}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="refresh-btn" onClick={loadBookings}>🔄 Refresh</button>
          </div>
        </div>
        {loading ? <div className="loading">Loading bookings...</div> : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Customer</th>
                  <th>Worker</th>
                  <th>Amount</th>
                  <th>Platform Fee</th>
                  <th>Worker Earning</th>
                  <th>Status</th>
                  <th>Refund</th>
                  <th>Scheduled</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(b => (
                  <tr key={b.id}>
                    <td>{b.display_id || `#${b.id}`}</td>
                    <td>
                      {b.customer ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{b.customer.name}</div>
                          <div style={{ fontSize: 12, color: '#1565c0', fontWeight: 500 }}>📞 {b.customer.phone || '—'}</div>
                        </div>
                      ) : `User #${b.customer_id}`}
                    </td>
                    <td>
                      {b.worker ? (
                        <div>
                          <div style={{ fontWeight: 600 }}>{b.worker.user?.name || 'Worker'}</div>
                          <div style={{ fontSize: 12, color: '#00695c', fontWeight: 500 }}>📞 {b.worker.user?.phone || '—'}</div>
                          <div style={{ fontSize: 11, color: '#999' }}>{b.worker.skill_category}</div>
                        </div>
                      ) : <span className="badge badge-warning">Unassigned</span>}
                    </td>
                    <td><strong>₹{Number(b.total_amount || 0).toFixed(2)}</strong></td>
                    <td>₹{Number(b.platform_fee || 0).toFixed(2)}</td>
                    <td>₹{Number(b.worker_earning || 0).toFixed(2)}</td>
                    <td>{statusBadge(b.status)}</td>
                    <td>
                      {b.payment?.status === 'refunded' ? (
                        <div>
                          <span className="badge badge-info">✅ Refunded</span>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#dc2626', marginTop: 2 }}>
                            ₹{Number(b.total_amount || 0).toFixed(2)}
                          </div>
                          <div style={{ fontSize: 10, color: '#999' }}>2–5 business days</div>
                        </div>
                      ) : b.status === 'cancelled' && b.payment?.status === 'paid' ? (
                        <span className="badge badge-warning">⏳ Pending</span>
                      ) : b.payment?.status === 'paid' ? (
                        <span className="badge badge-grey">Not Refunded</span>
                      ) : (
                        <span style={{ color: '#ccc' }}>—</span>
                      )}
                    </td>
                    <td>{b.scheduled_at?.substring(0, 16)}</td>
                    <td>
                      {assigningId === b.id ? (
                        <div style={{display:'flex', gap:4}}>
                          <input placeholder="Worker ID" value={workerId} onChange={e=>setWorkerId(e.target.value)} style={{width:80, padding:'4px 8px', border:'1px solid #ddd', borderRadius:4}} />
                          <button className="btn btn-sm btn-success" onClick={()=>handleAssign(b.id)}>✓</button>
                          <button className="btn btn-sm btn-danger" onClick={()=>setAssigningId(null)}>✗</button>
                        </div>
                      ) : (
                        <div style={{display:'flex', gap:4}}>
                          {!['completed','cancelled'].includes(b.status) && (
                            <button className="btn btn-sm btn-warning" onClick={()=>{setAssigningId(b.id); setWorkerId(b.worker_id?.toString() || '');}}>🔧 {b.worker_id ? 'Reassign' : 'Assign'}</button>
                          )}
                          <button className="btn btn-sm btn-danger" onClick={() => handleRefund(b.id)}>💸 Refund</button>
                          <button className="btn btn-sm" style={{background:'#666',color:'#fff'}} onClick={() => handleDelete(b.id)}>🗑️ Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
